import { ArrowUpRight, Check } from "lucide-react";
import { CAPABILITIES } from "../../data/content";
import { Reveal } from "./Reveal";

const Overline = ({ children, num }) => (
  <div className="flex items-center gap-4 mb-5">
    <span className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">{num}</span>
    <span className="h-px w-10 bg-white/20" />
    <span className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-400">{children}</span>
  </div>
);

const ExampleList = ({ items, cols = 1, className = "mt-5" }) => (
  <ul className={`${className} grid grid-cols-1 ${cols === 2 ? "sm:grid-cols-2" : ""} gap-x-6 gap-y-2.5`}>
    {items.map((ex) => (
      <li key={ex} className="flex items-start gap-2.5 font-body text-sm text-neutral-300">
        <Check size={15} strokeWidth={2.5} className="mt-0.5 shrink-0 text-neutral-500" />
        {ex}
      </li>
    ))}
  </ul>
);

const CapButton = ({ label, href }) => (
  <a
    href={href}
    data-testid={`capability-cta-${href.replace("#", "")}`}
    className="group mt-7 inline-flex items-center gap-2 font-sans font-bold uppercase tracking-widest text-sm text-white border-b-2 border-white/40 w-fit pb-1 hover:border-white transition-all"
  >
    {label}
    <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
  </a>
);

export const Capabilities = () => {
  const featured = CAPABILITIES.find((c) => c.featured) || CAPABILITIES[0];
  const rest = CAPABILITIES.filter((c) => c !== featured);
  const Fi = featured.icon;

  return (
    <section id="capabilities" data-testid="capabilities-section" className="relative border-t border-white/10 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Overline num="01">Capabilities</Overline>
        <h2 className="font-sans font-black uppercase tracking-tight text-4xl md:text-5xl lg:text-6xl text-white max-w-3xl leading-[0.95]">
          Capabilities Built <span className="metal-text">Around The Mission</span>
        </h2>
        <p className="mt-6 max-w-2xl font-body text-neutral-400 text-base md:text-lg leading-relaxed">
          Atlas Contracting Group LLC provides tactical equipment, operational supplies, safety products, and
          procurement support to government agencies. Explore our primary capability areas or contact us regarding a
          specialized requirement.
        </p>

        {/* Featured — Tactical & Defense */}
        <Reveal className="mt-14">
        <div id={featured.id} data-testid={`capability-card-${featured.id}`} className="scroll-mt-24 group relative grid grid-cols-1 lg:grid-cols-2 border border-white/15 bg-[#121212] overflow-hidden">
          <div className="relative min-h-[300px] lg:min-h-full overflow-hidden">
            <img src={featured.img} alt={featured.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-85 group-hover:scale-105 transition-all duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#121212] via-[#121212]/40 to-transparent" />
            <span className="absolute top-5 left-5 font-mono text-[11px] uppercase tracking-[0.2em] bg-white text-black px-3 py-1.5">{featured.tag}</span>
          </div>
          <div className="p-8 md:p-12">
            <Fi size={36} strokeWidth={1.5} className="text-white mb-6" />
            <h3 className="font-sans font-black uppercase tracking-tight text-3xl md:text-4xl text-white leading-none">{featured.title}</h3>
            <p className="mt-4 font-body text-neutral-400 text-base leading-relaxed">{featured.desc}</p>
            <ExampleList items={featured.examples} cols={2} />
            <CapButton label={featured.button} href={featured.href} />
          </div>
        </div>
        </Reveal>

        {/* Supporting capability cards */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          {rest.map((c, i) => {
            const Ci = c.icon;
            const wide = i === rest.length - 1 && rest.length % 2 === 1;
            return (
              <Reveal key={c.id} delay={(i % 2) * 0.12} className={`flex ${wide ? "md:col-span-2" : ""}`}>
              <div id={c.id} data-testid={`capability-card-${c.id}`} className={`scroll-mt-24 w-full group border border-white/10 bg-[#121212] flex hover:border-white/40 transition-all overflow-hidden ${wide ? "flex-col md:flex-row" : "flex-col"}`}>
                <div className={`relative overflow-hidden ${wide ? "h-44 md:h-auto md:w-2/5" : "h-44"}`}>
                  <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
                  <div className={`absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent ${wide ? "md:bg-gradient-to-r" : ""}`} />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <Ci size={30} strokeWidth={1.5} className="text-white mb-4" />
                  <h3 className="font-sans font-bold uppercase tracking-tight text-2xl text-white leading-tight">{c.title}</h3>
                  <p className="mt-3 font-body text-neutral-400 text-sm leading-relaxed">{c.desc}</p>
                  <ExampleList items={c.examples} cols={2} />
                  <div className="mt-auto"><CapButton label={c.button} href={c.href} /></div>
                </div>
              </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
